import { Link } from "react-router-dom";
import { ArrowRight, Baby, BriefcaseBusiness, CalendarHeart, Camera, Frame, Gift, Heart, Home, Palette, PawPrint, Sparkles } from "lucide-react";
import { FUNNEL_EVENTS, trackFunnelEvent } from "@/services/FunnelAnalytics";

const createCtaSource = "homepage_guides_cta";

const commercialHubs = [
  {
    to: "/photo-en-tableau",
    icon: Camera,
    label: "Photo en tableau",
    description:
      "Transformer une photo personnelle en visuel artistique, puis comprendre les étapes jusqu'au tableau imprimé.",
  },
  {
    to: "/tableau-photo-personnalise",
    icon: Home,
    label: "Tableau photo personnalisé",
    description:
      "Comprendre le parcours photo → style artistique → support imprimé avant de lancer la création.",
  },
  {
    to: "/idees-cadeaux-photo-personnalises",
    icon: Gift,
    label: "Idées cadeaux photo personnalisés",
    description:
      "Trouver le bon angle cadeau selon l'occasion : famille, couple, mariage, Noël ou grands-parents.",
  },
  {
    to: "/portrait-animal-personnalise",
    icon: PawPrint,
    label: "Portrait animal personnalisé",
    description:
      "Choisir une bonne photo de chien, chat ou compagnon et le style artistique le plus adapté.",
  },
  {
    to: "/portrait-chien-personnalise",
    icon: PawPrint,
    label: "Portrait de chien personnalisé",
    description:
      "Choisir une photo qui préserve le regard, les oreilles et la personnalité du chien avant de tester un style.",
  },
  {
    to: "/portrait-chat-personnalise",
    icon: PawPrint,
    label: "Portrait de chat personnalisé",
    description:
      "Choisir une photo qui préserve le regard, les moustaches et la personnalité du chat avant de tester un style.",
  },
  {
    to: "/portrait-cheval-personnalise",
    icon: PawPrint,
    label: "Portrait de cheval personnalisé",
    description:
      "Choisir un cadrage qui préserve le regard, la silhouette et la complicité avec le cavalier avant de tester un style.",
  },
  {
    to: "/portrait-couple-personnalise",
    icon: Heart,
    label: "Portrait de couple personnalisé",
    description:
      "Transformer un portrait complice, un souvenir de voyage ou un moment à célébrer en tableau artistique.",
  },
  {
    to: "/portrait-bebe-personnalise",
    icon: Baby,
    label: "Portrait de bébé personnalisé",
    description:
      "Choisir une photo de naissance, un premier sourire ou un moment familial et le style artistique adapté.",
  },
  {
    to: "/decoration-murale-personnalisee",
    icon: Frame,
    label: "Décoration murale personnalisée",
    description:
      "Accorder une photo, une orientation et un style artistique au salon, à la chambre ou à un espace familial.",
  },
  {
    to: "/cadeau-anniversaire-photo-personnalise",
    icon: CalendarHeart,
    label: "Cadeau anniversaire photo",
    description:
      "Trouver une idée cadeau anniversaire personnelle à partir d'une photo de famille, couple, ami ou animal.",
  },
  {
    to: "/cadeau-noel-photo-personnalise",
    icon: Sparkles,
    label: "Cadeau Noël photo",
    description:
      "Préparer un cadeau de Noël personnalisé à partir d'un souvenir familial, de couple, d'animal ou de maison.",
  },
  {
    to: "/cadeau-mariage-photo-personnalise",
    icon: CalendarHeart,
    label: "Cadeau mariage photo",
    description:
      "Transformer un souvenir de couple, cérémonie ou voyage en tableau personnalisé à offrir aux mariés.",
  },
  {
    to: "/cadeau-depart-retraite-photo-personnalise",
    icon: BriefcaseBusiness,
    label: "Cadeau départ retraite photo",
    description:
      "Transformer une photo d'équipe, un lieu de métier ou une passion en souvenir personnalisé pour un départ à la retraite.",
  },
];

const supportingGuides = [
  {
    to: "/blog/cadeau-mariage-original",
    label: "Cadeau de mariage original",
  },
  {
    to: "/blog/cadeau-grands-parents-photo-famille",
    label: "Cadeau grands-parents photo",
  },
  {
    to: "/blog/tableau-photo-animal-chien-chat",
    label: "Photo chien ou chat en tableau",
  },
  {
    to: "/styles/watercolor",
    label: "Style aquarelle",
  },
  {
    to: "/styles/oil-painting",
    label: "Peinture à l'huile",
  },
];

const SeoInternalLinksSection = () => {
  return (
    <section className="section-shell relative py-20">
      <div className="container mx-auto px-6">
        <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-3xl">
            <p className="eyebrow mb-3">Guides utiles</p>
            <h2 className="section-title text-foreground">Explorer les bons chemins avant de créer.</h2>
            <p className="mt-4 text-lg leading-8 text-foreground/65">
              Pour aider chaque visiteur à passer d'une intention précise à la création, ces pages relient les principaux besoins : tableau décoratif, cadeau personnalisé et portrait d'animal.
            </p>
          </div>
          <Link
            to={`/create?source=${createCtaSource}`}
            onClick={() => trackFunnelEvent(FUNNEL_EVENTS.homepageCtaClicked, { source: createCtaSource })}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground shadow-card transition-smooth hover:gap-3"
          >
            Créer avec ma photo
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {commercialHubs.map((hub) => (
            <Link
              key={hub.to}
              to={hub.to}
              className="group rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md transition-smooth hover:-translate-y-1 hover:bg-white/85"
            >
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                <hub.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{hub.label}</h3>
              <p className="mt-4 text-sm leading-7 text-foreground/65">{hub.description}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                Lire le guide
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-8 rounded-[1.8rem] border border-white/65 bg-white/55 p-6 shadow-card backdrop-blur-md">
          <div className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.22em] text-foreground/45">
            <Palette className="h-4 w-4 text-primary" />
            Articles et styles liés
          </div>
          <div className="flex flex-wrap gap-3">
            {supportingGuides.map((guide) => (
              <Link
                key={guide.to}
                to={guide.to}
                className="rounded-full border border-primary/12 bg-white/70 px-4 py-2 text-sm text-foreground/66 transition-smooth hover:border-primary/25 hover:text-primary"
              >
                {guide.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeoInternalLinksSection;
